import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ContentGetListInvoice, TipeReturn } from "../../constants/types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import defaultAxios, { AxiosError } from "axios";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTransaksi } from "../../context/transaksiContext";
import axios from "../../services/axios";
import Swal from "sweetalert2";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Delete from "@mui/icons-material/Delete";
import LoadingButton from "@mui/lab/LoadingButton";

interface IModalAjuanReturRefundPenjualanProps {
    open: boolean;
    onClose: () => void;
    data: ContentGetListInvoice;
}

interface IValues {
    details: {
        transaksiDetailId: number;
        tipe: string;
        jumlah: number;
    }[];
}

const ModalAjuanReturRefundPenjualan = ({
    open,
    onClose,
    data,
}: IModalAjuanReturRefundPenjualanProps) => {
    const { setFetchingItems } = useTransaksi();
    const [isButtonLoading, setIsButtonLoading] = useState(false);

    const initialValues = useMemo(
        () => ({
            details: data.detailItem.map((li) => ({
                transaksiDetailId: li.idDetail,
                tipe: "RETUR",
                jumlah: li.jumlahItem,
            })),
        }),
        [data.detailItem],
    );

    const { handleSubmit, control, reset } = useForm<IValues>({
        // resolver: yupResolver(schema),
        defaultValues: initialValues,
    });
    const { fields, remove } = useFieldArray({
        control,
        name: "details",
    });

    const onSubmit = async (values: IValues) => {
        setIsButtonLoading(true);
        try {
            const body = {
                details: values.details,
            };
            console.log("body", body);
            const { status } = await axios.post(`/api/transaksi-refund`, body);
            if (status === 200) {
                Swal.fire({
                    title: `Pengajuan retur/refund ${data.nomorInvoice} berhasil ditambahkan`,
                    position: "top-end",
                    showConfirmButton: false,
                    icon: "success",
                    toast: true,
                    timer: 3000,
                    timerProgressBar: true,
                    showCloseButton: true,
                    customClass: {
                        container: "my-swal",
                    },
                });
                setFetchingItems();
                onClose();
            }
            setIsButtonLoading(false);
        } catch (err) {
            console.error(err);
            handleErrorResponse(err);
            setIsButtonLoading(false);
        }
    };

    const handleErrorResponse = useCallback((error: any) => {
        if (defaultAxios.isAxiosError(error)) {
            const serverError = error as AxiosError<any>;
            if (serverError && serverError.response) {
                Swal.fire({
                    title: `${serverError.response.data.message}`,
                    position: "top-end",
                    showConfirmButton: false,
                    icon: "error",
                    toast: true,
                    timer: 3000,
                    timerProgressBar: true,
                    showCloseButton: true,
                    customClass: {
                        container: "my-swal",
                    },
                });
            } else {
                Swal.fire({
                    title: "Terjadi Kesalahan!",
                    position: "top-end",
                    showConfirmButton: false,
                    icon: "error",
                    toast: true,
                    timer: 3000,
                    timerProgressBar: true,
                    showCloseButton: true,
                    customClass: {
                        container: "my-swal",
                    },
                });
            }
        }
    }, []);

    useEffect(() => {
        if (open) {
            reset(initialValues);
        }
    }, [initialValues, open, reset]);

    return (
        <Dialog
            maxWidth="sm"
            fullWidth={true}
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { borderRadius: 1 },
            }}
        >
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton onClick={onClose}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" fontWeight="bold">
                        Pengajuan Retur/Refund Penjualan
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={4}>
                    {fields.length > 0 ? (
                        fields.map((li, idx) => (
                            <Stack key={li.id} spacing={2}>
                                <Stack>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        // justifyContent="space-between"
                                        spacing={2}
                                        mb={1}
                                    >
                                        <Typography fontWeight={600}>
                                            {idx + 1}.{" "}
                                            {data.detailItem[idx].namaItem}
                                        </Typography>
                                        <Button
                                            disableElevation
                                            variant="outlined"
                                            onClick={() => remove(idx)}
                                            size="small"
                                            color="buttonred"
                                            sx={{
                                                minWidth: "unset",
                                                padding: 0.5,
                                                width: 24,
                                                height: 24,
                                            }}
                                        >
                                            <Delete
                                                sx={{
                                                    width: 16,
                                                    height: 16,
                                                }}
                                            />
                                        </Button>
                                    </Stack>
                                    <Typography>Tipe:</Typography>
                                    <Controller
                                        control={control}
                                        name={`details.${idx}.tipe`}
                                        rules={{
                                            required: "Kolom wajib diisi",
                                        }}
                                        render={({
                                            field: { onChange, value },
                                            fieldState,
                                        }) => (
                                            <FormControl fullWidth>
                                                <Select
                                                    placeholder="Pilih Tipe"
                                                    margin="dense"
                                                    onChange={onChange}
                                                    size="small"
                                                    value={value}
                                                    error={Boolean(
                                                        fieldState.error,
                                                    )}
                                                >
                                                    <MenuItem
                                                        value={TipeReturn.Retur}
                                                    >
                                                        Retur
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={
                                                            TipeReturn.Refund
                                                        }
                                                    >
                                                        Refund
                                                    </MenuItem>
                                                </Select>
                                                <FormHelperText
                                                    error={Boolean(
                                                        fieldState.error,
                                                    )}
                                                >
                                                    {fieldState.error
                                                        ? fieldState.error
                                                              .message
                                                        : ""}
                                                </FormHelperText>
                                            </FormControl>
                                        )}
                                    />
                                </Stack>
                                <Stack>
                                    <Typography>
                                        Jumlah Retur/Refund:
                                    </Typography>
                                    <Controller
                                        name={`details.${idx}.jumlah`}
                                        control={control}
                                        rules={{
                                            required: "Kolom wajib diisi",
                                            min: {
                                                value: 1,
                                                message:
                                                    "Minimal retur/refund adalah 1 buah", // JS only: <p>error message</p> TS only support string
                                            },
                                            max: {
                                                value: data.detailItem[idx]
                                                    .jumlahItem,
                                                message: "Stok maksimal", // JS only: <p>error message</p> TS only support string
                                            },
                                        }}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                type="number"
                                                variant="outlined"
                                                placeholder="Jumlah Retur/Refund"
                                                size="small"
                                                onWheel={(e) => {
                                                    const target =
                                                        e.target as HTMLButtonElement;
                                                    if (target) {
                                                        target.blur();
                                                        e.stopPropagation();
                                                        setTimeout(() => {
                                                            target.focus();
                                                        }, 0);
                                                    }
                                                }}
                                                InputProps={{ readOnly: true }}
                                                error={!!fieldState?.error}
                                                helperText={
                                                    fieldState?.error &&
                                                    fieldState?.error.message
                                                }
                                                {...field}
                                            />
                                        )}
                                    />
                                </Stack>
                            </Stack>
                        ))
                    ) : (
                        <Stack
                            justifyContent="center"
                            alignItems="center"
                            height={200}
                        >
                            <Typography fontSize={16}>
                                Barang tidak ditemukan
                            </Typography>
                        </Stack>
                    )}
                </Stack>
                <Stack mt={3} direction="row" justifyContent="flex-end">
                    <LoadingButton
                        disabled={fields.length === 0}
                        loading={isButtonLoading}
                        loadingPosition="center"
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                    >
                        Simpan
                    </LoadingButton>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default ModalAjuanReturRefundPenjualan;
